<template>
    <div>
        <!-- Section header -->
        <div class="mb-4">
            <!-- Title with the due date and the section's actions menu to the side. -->
            <div class="flex items-center justify-between gap-2">
                <h2 class="scaffold-body truncate">{{ otherTitle }}</h2>
                <div class="flex items-center gap-2 shrink-0">
                    <span v-if="dateLabel" class="scaffold-body-small text-ink-muted whitespace-nowrap">{{ dateLabel }}</span>
                    <!-- The opt-out label is long: keep the menu on screen (8px collision padding each side) and let it wrap. -->
                    <BaseDropdownMenu v-if="menuSections.length" :sections="menuSections" content-class="max-w-[calc(100vw-1rem)]" @select="handleMenuSelect">
                        <template #trigger>
                            <BaseButton ref="menuTrigger" type="button" theme="tertiary" size="sm" icon-only :aria-label="t('moments.people.sectionActionsMenu')">
                                <i class="bi bi-three-dots" aria-hidden="true" />
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                </div>
            </div>
            <p v-if="otherSubtitle" class="scaffold-body-small text-ink-muted mt-1 whitespace-pre-line">{{ otherSubtitle }}</p>
        </div>
        <div class="flex flex-col gap-3">
            <!-- Empty state: user has opted into reviewing team members but hasn't added any yet -->
            <BaseCard v-if="!Object.keys(reviewsByRelationship).length" padding="lg" class="flex flex-col items-center justify-center gap-3 text-center">
                <i class="bi bi-people text-3xl text-ink-muted" aria-hidden="true"></i>
                <div class="body-small text-ink-muted">{{ t("moments.people.emptyState") }}</div>
                <BaseButton v-if="canChangeTeam" theme="secondary" class="flex items-center gap-2" @click="$emit('edit-list')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    {{ t("moments.people.addTeamMembers") }}
                </BaseButton>
            </BaseCard>
            <!-- Grouped reviews by relationship -->
            <div v-for="(relationshipReviews, relationshipId) in reviewsByRelationship" :key="relationshipId" class="flex flex-col gap-3">
                <!-- My People Card -->
                <BaseCard padding="none" class="flex flex-col overflow-hidden">
                    <!-- Sits outside the collapsible's header trigger: it holds its own buttons, which cannot nest inside one. -->
                    <div
                        v-if="relationshipReviews[0].relationship?.needs_confirmation && canChangeTeam"
                        data-testid="hris-team-member-confirmation"
                        class="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3 rounded-t-xl bg-accent-soft border-b border-border">
                        <p class="body-small text-ink grow">
                            {{ t("components.moments.reviewTeam.addedConfirmationPrompt", { name: relationshipReviews[0].relationship?.reviewee_name }) }}
                        </p>
                        <div class="flex gap-2 shrink-0">
                            <BaseButton theme="neutral" size="sm" :disabled="isResolvingTeamMember" @click="$emit('decline-relationship', String(relationshipId))">
                                {{ t("components.moments.reviewTeam.removeAddedMember") }}
                            </BaseButton>
                            <BaseButton theme="primary" size="sm" :disabled="isResolvingTeamMember" @click="$emit('confirm-relationship', String(relationshipId))">
                                {{ t("components.moments.reviewTeam.keepAddedMember") }}
                            </BaseButton>
                        </div>
                    </div>
                    <BaseCollapsible row-trigger :open="!!expandedCards[relationshipId]" :unmount-on-hide="false" @update:open="toggleCard(relationshipId)">
                        <template #trigger>
                            <div class="flex w-full justify-between items-center px-5 py-5">
                                <div class="flex flex-col gap-1">
                                    <div class="body-bold" :class="isMomentReadOnly ? 'text-ink-muted' : 'text-ink'">
                                        {{ relationshipReviews[0].relationship?.reviewee_name }}
                                    </div>
                                    <div v-if="relationshipReviews[0].relationship?.reviewee_job_title" class="body-small text-ink-muted">
                                        {{ relationshipReviews[0].relationship?.reviewee_job_title }}
                                    </div>
                                    <div v-if="relationshipReviews[0].relationship?.seeded_from_hris" class="details flex items-center gap-2 text-ink-muted">
                                        <i class="bi bi-diagram-3" aria-hidden="true"></i>
                                        {{ t("components.moments.reviewTeam.seededFromReportingLine") }}
                                    </div>
                                </div>
                                <div class="flex items-center gap-3">
                                    <StatusChip
                                        v-if="isSectionLocked || !(isMomentReadOnly && getReviewGroupStatusState(relationshipReviews) === 'neutral')"
                                        size="2xs"
                                        :state="isSectionLocked ? 'neutral' : getReviewGroupStatusState(relationshipReviews)">
                                        {{ isSectionLocked ? t("moments.people.comingSoon") : getReviewGroupStatus(relationshipReviews) }}
                                    </StatusChip>
                                    <i :class="expandedCards[relationshipId] ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-ink-muted p-1" aria-hidden="true"></i>
                                </div>
                            </div>
                        </template>
                        <template #content>
                            <div class="flex flex-col gap-1">
                                <HorizontalDivider class="my-0!" />
                                <PerformanceManagementCTA
                                    v-for="review in relationshipReviews"
                                    :key="review.id"
                                    :review="review"
                                    class="px-5"
                                    :is-loading="!!currentReviewId && currentReviewId === review.id"
                                    :is-disabled="!!currentReviewId && currentReviewId !== review.id"
                                    :is-moment-read-only="isMomentReadOnly"
                                    :is-coming-soon="isSectionLocked"
                                    @cta-convo-clicked="(reviewId) => $emit('cta-convo-clicked', reviewId)"
                                    @completion-toggled="(reviewId, complete) => $emit('completion-toggled', reviewId, complete)" />
                            </div>
                        </template>
                    </BaseCollapsible>
                </BaseCard>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatSectionDateLabel, isStartDateInFuture } from "~vue/composables/useCtaDateRange";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { logUserInteraction } from "~vue/utils/logUtils";
import { sortBy, uniq } from "lodash";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { getReviewGroupStatus, getReviewGroupStatusState } from "./composables/useReviewGroupStatus";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "./constants";
import PerformanceManagementCTA from "./PerformanceManagementCTA.vue";

const props = withDefaults(
    defineProps<{
        dueDate?: string | null;
        startDate?: string | null;
        otherTitle?: string;
        otherSubtitle?: string;
        /** Whether the user can still opt out of reviewing team members for this moment. */
        canStopReviewing?: boolean;
        reviews: DecoratedPerformanceReview[];
        momentId?: string | number | null;
        currentReviewId?: string | null;
        isMomentReadOnly?: boolean;
        /** An answer to a detected-team-member prompt is in flight; every prompt waits for it. */
        isResolvingTeamMember?: boolean;
        /** False when the organization keeps the review team to what HRIS seeds. */
        canEditTeam?: boolean;
    }>(),
    {
        dueDate: null,
        startDate: null,
        otherTitle: "",
        otherSubtitle: "",
        canStopReviewing: false,
        momentId: null,
        currentReviewId: null,
        isResolvingTeamMember: false,
        canEditTeam: true,
    },
);

const emit = defineEmits(["edit-list", "stop-reviewing-others", "cta-convo-clicked", "completion-toggled", "confirm-relationship", "decline-relationship"]);

const { t } = useI18n();

const dateLabel = computed(() => formatSectionDateLabel(props.startDate, props.dueDate));

// Section hasn't opened yet — disable/grey its CTAs until the start date arrives.
const isSectionLocked = computed(() => isStartDateInFuture(props.startDate));

onMounted(() => {
    if (props.reviews?.length) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_section_shown`, { moment_id: props.momentId });
    }
});

const reviewsByRelationship = computed(() => {
    const reviewsWithRelationships = props.reviews.filter((review) => !!review.relationship);
    const result: Record<string, DecoratedPerformanceReview[]> = {};
    const relationshipIds = uniq(sortBy(reviewsWithRelationships, (review) => review.relationship?.reviewee_name).map((review) => review.relationship?.id));
    for (const relationshipId of relationshipIds) {
        const relationshipReviews = reviewsWithRelationships.filter((review) => review.relationship?.id === relationshipId);
        result[String(relationshipId)] = sortBy(relationshipReviews, (review) => review.cta?.id); // TODO: Sort this by something admin-specified rather than CTA ID
    }
    return result;
});

const canChangeTeam = computed(() => props.canEditTeam && !props.isMomentReadOnly);
const shouldShowEditTeam = computed(() => canChangeTeam.value && Object.keys(reviewsByRelationship.value).length > 0);

const menuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (shouldShowEditTeam.value) {
        items.push({ id: "edit-team", type: "button", label: t("moments.people.editTeam") });
    }
    if (props.canStopReviewing) {
        items.push({ id: "stop-reviewing-others", type: "button", label: t("moments.reviewSwitcher.stopReviewingOthers"), itemClass: "whitespace-normal" });
    }
    return items.length ? [{ id: "actions", items }] : [];
});

const menuTrigger = useTemplateRef<InstanceType<typeof BaseButton>>("menuTrigger");
defineExpose({ focusMenu: () => (menuTrigger.value?.$el as HTMLElement | undefined)?.focus() });

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "edit-team") {
        emit("edit-list");
    } else if (item.id === "stop-reviewing-others") {
        emit("stop-reviewing-others");
    }
};

// Track expanded/collapsed state for each colleague card
const expandedCards = ref<Record<string | number, boolean>>({});

const toggleCard = (relationshipId: number | string) => {
    expandedCards.value[relationshipId] = !expandedCards.value[relationshipId];
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_member_card_toggled`, {
        relationship_id: relationshipId,
        new_state: expandedCards.value[relationshipId] ? "open" : "closed",
    });
};

// Initialize first colleague card as expanded when relationships load
watch(reviewsByRelationship, (data) => {
    if (data) {
        const firstRelationshipId = Object.keys(data)[0];
        if (firstRelationshipId && expandedCards.value[firstRelationshipId] === undefined) {
            expandedCards.value[firstRelationshipId] = true;
        }
    }
});
</script>
